import { Button, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import IResponse from '../../../interfaces/response.ts';
import { useEffect, useState } from 'react';
import IosSwitch from '../../common/IosSwitch.tsx';
import Upload from '../../common/Upload.tsx';
import { IBlogs } from '../../../interfaces/IBlogs.ts';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

export default function CreateBlogs() {
	const { t } = useTranslation();
	const [loading, setLoading] = useState<boolean>(false);
	const [categoryId, setCategoryId] = useState<number>(1);
	const [blogs, setBlogs] = useState<IBlogs[]>();
	const [type, setType] = useState('post');
	const [files, setFiles] = useState<(File | string)[]>([]);
	const [isSwitchEnabled, setIsSwitchEnabled] = useState<boolean>(true);
	const { id } = useParams();

	const navigation = useNavigate();

	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsSwitchEnabled(event.target.checked);
	};
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setType((event.target as HTMLInputElement).value);
	};
	const blogsSchema = z.object({
		description: z.string().nonempty(t('invalid_description_message')),
		summary: z.string(),
		title: z.string().min(3, t('invalid_min_slug_message')),
		slug: z.string().min(3, t('invalid_min_slug_message')),
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
		watch,
		control,
	} = useForm<z.infer<typeof blogsSchema>>({
		resolver: zodResolver(blogsSchema),
	});
	useEffect(() => {
		axios<IResponse<IBlogs[]>>({
			url: `/options/post_category`,
		}).then(res => {
			if (res.data.success) {
				setBlogs(res.data.data);
			}
		});
		if (id) {
			axios<IResponse<IBlogs>>({
				url: `/admin/posts/${id}`,
				loading: true,
			}).then(res => {
				if (res.data.success) {
					setValue('title', res.data.data.title);
					setValue('slug', res.data.data.slug);
					setValue('description', res.data.data.content);
					setValue('summary', res.data.data.summary);
					setType(res.data.data.type);
					setFiles([res.data.data.image]);
					setCategoryId(res.data.data.category_id);
					setIsSwitchEnabled(res.data.data.is_active === 1);
				}
			});
		}
	}, []);
	const handleNewblogs = (data: z.infer<typeof blogsSchema>) => {
		setLoading(true);
		const formData = new FormData();
		formData.append('title', data.title);
		formData.append('content', data.description);
		formData.append('category_id', categoryId + '');
		formData.append('summary', data.summary + '');
		formData.append('type', type);
		formData.append('is_active', isSwitchEnabled ? '1' : '0');
		formData.append('slug', data.slug);
		formData.append('blocks', '');
		files.forEach(file => {
			if (file instanceof File) {
				formData.append('image', file);
			}
		});
		if (id) {
			formData.append('_method', 'PUT');
		}
		axios<IResponse<IBlogs>>({
			url: id ? `/admin/posts/${id}` : '/admin/posts',
			method: 'POST',
			data: formData,
		})
			.then(r => {
				if (r?.data?.success) {
					navigation('/admin/blog');
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};
	const handleSelectCategory = (e: any) => {
		setCategoryId(e.target.value);
	};
	const handleReset = () => {
		reset();
	};

	return (
		<div className={'w-full rounded bg-white p-5'}>
			<form onSubmit={handleSubmit(handleNewblogs)}>
				<Typography className={'text-text-xl font-bold text-gray-900'}>{id ? t('edit_new_blog') : t('create_new_blog')}</Typography>
				<div className={'flex gap-x-4'}>
					<TextField className={'mt-6'} variant={'outlined'} fullWidth label={t('title')} InputLabelProps={{ shrink: !!watch('title') }} {...register('title')} error={!!errors.title} helperText={errors.title?.message} />
					<TextField className={'mt-6'} variant={'outlined'} fullWidth label={t('slug')} InputLabelProps={{ shrink: !!watch('slug') }} {...register('slug')} error={!!errors.slug} helperText={errors.slug?.message} />
				</div>
				<TextField className={'mt-6'} variant={'outlined'} fullWidth label={t('summary')} InputLabelProps={{ shrink: !!watch('summary') }} {...register('summary')} error={!!errors.summary} helperText={errors.summary?.message} multiline rows={3} />
				<div className={'mt-6'}>
					<Typography>{t('description')}</Typography>
					<Controller
						name="description"
						control={control}
						render={({ field }) => (
							<CKEditor
								editor={ClassicEditor}
								data={field.value || ''}
								config={{
									toolbar: ['bold', 'italic', 'fontSize', 'fontColor', 'fontBackgroundColor'],
									fontSize: {
										options: [9, 11, 13, 'default', 17, 19, 21],
									},
								}}
								onReady={(editor: any) => {
									console.log(editor, 'Editor ready');
								}}
								onChange={(editor: any) => {
									const data = editor.getData();
									field.onChange(data);
								}}
							/>
						)}
					/>
				</div>
				<div className={'flex gap-12'}>
					<div className={'mt-8 w-full'}>
						<FormControl fullWidth className={'hidden'}>
							<InputLabel>{t('category')}</InputLabel>
							<Select value={categoryId} label={t('category')} onChange={handleSelectCategory}>
								{blogs?.map((cat, index) => (
									<MenuItem key={index} value={cat.id}>
										{cat.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Divider className={'my-4 hidden'} />
						<Typography className={'text-sm text-gray-900'}>{t('Content-type')}</Typography>

						<FormControl>
							<RadioGroup className={'flex flex-row justify-start gap-x-6'} value={type} onChange={handleChange}>
								<FormControlLabel value={'page'} control={<Radio className={'text-base'} />} label={<Typography> {t('page')}</Typography>} />
								<FormControlLabel value={'post'} control={<Radio className={'text-base'} />} label={<Typography> {t('post')}</Typography>} />
							</RadioGroup>
						</FormControl>

						<Divider className={'mb-4 mt-4'} />

						<IosSwitch label={t('active')} checked={true} onChange={handleSwitchChange} color="success" />
					</div>
					<div className={'w-full'}>
						<Typography className={'mt-3'}>{t('image-our-video')}</Typography>
						<Upload text={t('upload_image')} setFiles={setFiles} files={files} className={'flex w-full border-2 border-gray-200 bg-grey-100'} />
					</div>
				</div>

				<div className="mb-8 mt-6 flex flex-row gap-3">
					<LoadingButton loading={loading} variant="contained" className="my-2" type="submit">
						{t('submit')}
					</LoadingButton>
					<Button onClick={handleReset} variant="outlined" className="my-2 border-grey-400 text-grey-400">
						{t('cancel')}
					</Button>
				</div>
			</form>
		</div>
	);
}
